<template>
  <!-- Off-canvas menu for mobile -->
  <div class="md:hidden">
    <transition
      enter-active-class="ease-linear duration-300"
      leave-active-class="ease-linear duration-300"
    >
      <div v-show="open" class="fixed inset-0 flex z-40">
        <!--
        Off-canvas menu overlay, show/hide based on off-canvas menu state.
        -->
        <transition
          enter-active-class="transition-opacity ease-linear duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity ease-linear duration-300"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-show="open" class="fixed inset-0">
            <div class="absolute inset-0 bg-gray-600 opacity-75"></div>
          </div>
        </transition>

        <!--
        Off-canvas menu, show/hide based on off-canvas menu state.
        -->
        <transition
          enter-active-class="transition ease-in-out duration-300 transform"
          enter-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition ease-in-out duration-300 transform"
          leave-class="translate-x-0"
          leave-to-class="-translate-x-full"
        >
          <div v-show="open" class="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
            <div class="absolute top-0 right-0 -mr-14 p-1">
              <button
                class="flex items-center justify-center h-12 w-12 rounded-full focus:outline-none focus:bg-gray-600"
                aria-label="Close sidebar"
                @click="$emit('close')"
                v-show="open"
              >
                <svg
                  class="h-6 w-6 text-white"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div class="flex-1 h-0 pt-3 pb-4 overflow-y-auto">
              <div class="flex-shrink-0 flex items-center px-2">
                <account v-slot:default="{ currentAccount, loading, accounts }">
                  <b-dropdown v-slot:default="{ isOpen, close, open }">
                    <div class="min-w-0">
                      <button
                        @click="open"
                        class="px-2 py-2 flex items-center justify-between w-full text-gray-200 focus:outline-none focus:text-white focus:bg-gray-700 transition ease-in-out duration-150 rounded"
                        :class="{ 'bg-gray-700': isOpen }"
                      >
                        <div class="flex items-center min-w-0">
                          <div
                            v-if="!loading"
                            class="flex flex-shrink-0 w-10 h-10 bg-gray-900 rounded justify-center items-center text-2xl"
                          >{{ currentAccount.company_name.charAt(0) }}</div>
                          <div class="ml-4 pr-4 w-64 text-left min-w-0" v-if="!loading">
                            <div class="truncate">{{ currentAccount.company_name }}</div>
                            <div class="text-sm font-light">ID #{{ currentAccount.id }}</div>
                          </div>
                        </div>

                        <font-awesome-icon icon="chevron-down"></font-awesome-icon>
                      </button>

                      <transition leave-active-class="ease-in duration-200">
                        <div
                          v-show="isOpen"
                          class="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center z-10"
                        >
                          <!--
            Background overlay, show/hide based on modal state.
                          -->
                          <transition
                            enter-active-class="ease-out duration-300"
                            enter-class="opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="ease-in duration-200"
                            leave-class="opacity-100"
                            leave-to-class="opacity-0"
                          >
                            <div
                              v-if="isOpen"
                              @click="close"
                              class="fixed inset-0 transition-opacity"
                            >
                              <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>
                          </transition>

                          <!--
              Modal panel, show/hide based on modal state.
                          -->
                          <transition
                            enter-active-class="ease-out duration-300"
                            enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                            leave-active-class="opacity-100 translate-y-0 sm:scale-100"
                            leave-class="opacity-100 translate-y-0 sm:scale-100"
                            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                          >
                            <div
                              v-if="isOpen"
                              class="relative bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full"
                              role="dialog"
                              aria-modal="true"
                              aria-labelledby="modal-headline"
                            >
                              <div class="flex justify-between px-3 py-4 bg-gray-100">
                                <h1 class="font-medium">Kontoeinstellungen</h1>
                                <button
                                  @click="close"
                                  type="button"
                                  class="text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150"
                                  aria-label="Close"
                                >
                                  <span class="h-6 w-6">
                                    <font-awesome-icon class="text-xl" :icon="['far', 'times']" />
                                  </span>
                                </button>
                              </div>
                              <div class="sm:flex sm:items-start">
                                <account-settings class="w-full" :user="user" :accounts="accounts" />
                              </div>
                            </div>
                          </transition>
                        </div>
                      </transition>
                    </div>
                  </b-dropdown>
                </account>
              </div>
              <nav class="mt-4 px-2">
                <slot />
              </nav>
            </div>
          </div>
        </transition>
        <div class="flex-shrink-0 w-14">
          <!-- Force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Account from "@/components/Account";
import BDropdown from "@/components/BDropdown";
import AccountSettings from "./AccountSettings";

export default {
  props: ["open"],
  components: { Account, BDropdown, AccountSettings },
  computed: {
    user() {
      return this.$store.state.authentication.keycloak;
    }
  },
  mounted() {
    this.$watch(
      () => this.$route.path,
      () => this.$emit("close")
    );
  }
};
</script>
